// Keuzevelden met mensen (owner-keuze "velden" = a, 04-11, pagina Sm7tnHiK…):
// Avatar · PersoonVeld (zoeken, met een leeg-melding) · PersoonChip · KeuzeKaarten
// (in plaats van rondjes) · Veldblok + Invoer · Schakelaar · Notitie · Foutmelding ·
// PlatformStijl (het kader voor een oud scherm).
//
// Los van één scherm: Plannen, Gebruikers en Delen gebruiken ze nu, de pagina per
// plan met het blok Mensen straks. Alleen klassen uit mensen.css (DESIGN.md regel 1).

import type { LucideIcon } from "lucide-react";
import { Search, X } from "lucide-react";
import {
  type InputHTMLAttributes,
  type KeyboardEvent,
  type ReactNode,
  useId,
  useLayoutEffect,
  useState,
} from "react";

import tokens from "../tokens.css?inline";
import "../bouwstenen.css";
import "../mensen.css";
import { Icoon, Knop } from "./basis";

/** Eén persoon in een keuzeveld. `sub` is de regel eronder: rol en propositie. */
export type Persoon = { id: string; naam: string; email?: string; sub?: string };

/** "Sanne de Vries" → "SV", "Tess (AM Techniek)" → "TE", "bea@x.nl" → "BE". */
export function initialen(naam: string): string {
  const woorden = naam
    .replace(/\(.*?\)/g, " ")
    .replace(/@.*/, "")
    .split(/[\s._+-]+/)
    .filter(Boolean);
  if (!woorden.length) return "?";
  const eerste = woorden[0]!;
  const tweede = woorden.length > 1 ? woorden.at(-1)![0] : (eerste[1] ?? "");
  return `${eerste[0]}${tweede}`.toUpperCase();
}

export function Avatar({ naam, groot = false }: { naam: string; groot?: boolean }) {
  return (
    <span className={`s-av${groot ? " s-av--groot" : ""}`} aria-hidden="true">
      {initialen(naam)}
    </span>
  );
}

/** Avatar, naam en de regel eronder (rol · propositie, of het e-mailadres). */
export function PersoonRegel({ persoon, rechts }: { persoon: Persoon; rechts?: ReactNode }) {
  return (
    <span className="s-persoon">
      <Avatar naam={persoon.naam} />
      <span className="s-persoon__tekst">
        <span className="s-persoon__naam">{persoon.naam}</span>
        {persoon.sub && <span className="s-persoon__sub">{persoon.sub}</span>}
      </span>
      {rechts}
    </span>
  );
}

const past = (p: Persoon, q: string) =>
  !q || [p.naam, p.email ?? "", p.sub ?? ""].some((t) => t.toLowerCase().includes(q));

/**
 * Kies één persoon door te zoeken op naam of e-mail. Is er niemand om uit te kiezen,
 * dan staat er een zin (`leeg`) in plaats van een lege lijst. Met `gekozen` toont het
 * veld die persoon, met "Wissel" zolang het niet `vast` staat.
 */
export function PersoonVeld({
  label,
  personen,
  gekozen = null,
  onKies,
  onWissel,
  leeg,
  placeholder = "Zoek op naam of e-mail",
  vast = false,
}: {
  /** De toegankelijke naam van het veld ("AM", "Meelezer toevoegen"). */
  label: string;
  personen: readonly Persoon[];
  gekozen?: Persoon | null;
  onKies: (p: Persoon) => void;
  /** Haalt de gekozen persoon weg; zonder deze functie geen "Wissel". */
  onWissel?: () => void;
  /** Wat er staat als er niemand te kiezen is. */
  leeg: ReactNode;
  placeholder?: string;
  vast?: boolean;
}) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [actief, setActief] = useState(0);
  const id = useId();

  if (gekozen) {
    return (
      <div className="s-persoonveld s-persoonveld--gekozen" data-lat="persoon">
        <PersoonRegel
          persoon={gekozen}
          rechts={
            !vast && onWissel ? (
              <Knop variant="link" className="s-persoon__actie" aria-label={`${label}: wissel ${gekozen.naam}`} onClick={onWissel}>
                Wissel
              </Knop>
            ) : undefined
          }
        />
      </div>
    );
  }
  if (vast) return <p className="s-notitie">Niemand gekozen.</p>;
  if (!personen.length) {
    return (
      <div className="s-notitie" role="note" aria-label={label}>
        {leeg}
      </div>
    );
  }

  const zoek = q.trim().toLowerCase();
  const lijst = personen.filter((p) => past(p, zoek));
  const kies = (p: Persoon) => {
    onKies(p);
    setQ("");
    setOpen(false);
    setActief(0);
  };
  const toets = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (!open) setOpen(true);
      else setActief((i) => Math.min(i + 1, lijst.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActief((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter" && open && lijst[actief]) {
      e.preventDefault();
      kies(lijst[actief]!);
    } else if (e.key === "Escape" && open) {
      // Eerst de lijst dicht, pas bij een tweede Esc het venster.
      e.preventDefault();
      e.stopPropagation();
      setOpen(false);
    }
  };

  return (
    <div className="s-persoonveld">
      <div className="s-invoer s-invoer--zoek">
        <Icoon als={Search} klein />
        <input
          role="combobox"
          aria-label={label}
          aria-expanded={open}
          aria-controls={`${id}-lijst`}
          aria-autocomplete="list"
          aria-activedescendant={open && lijst[actief] ? `${id}-${actief}` : undefined}
          value={q}
          placeholder={placeholder}
          autoComplete="off"
          onFocus={() => setOpen(true)}
          onClick={() => setOpen(true)}
          onBlur={() => setOpen(false)}
          onChange={(e) => {
            setQ(e.target.value);
            setActief(0);
            setOpen(true);
          }}
          onKeyDown={toets}
        />
      </div>
      {open && (
        <ul className="s-persoonlijst" id={`${id}-lijst`} role="listbox" aria-label={label} data-lat="zweeft">
          {lijst.length ? (
            lijst.map((p, i) => (
              <li
                key={p.id}
                id={`${id}-${i}`}
                role="option"
                aria-selected={i === actief}
                className="s-persoonlijst__optie"
                // Het veld houdt de focus, anders sluit de lijst vóór de klik.
                onMouseDown={(e) => e.preventDefault()}
                onMouseEnter={() => setActief(i)}
                onClick={() => kies(p)}
              >
                <PersoonRegel persoon={p} rechts={p.email && p.email !== p.naam ? <span className="s-persoon__email">{p.email}</span> : undefined} />
              </li>
            ))
          ) : (
            <li className="s-persoonlijst__niets">Niemand gevonden met “{q.trim()}”.</li>
          )}
        </ul>
      )}
    </div>
  );
}

/** Een gekozen persoon als chip, eventueel met een kruisje om weg te halen. */
export function PersoonChip({ persoon, onWeg, wegLabel }: { persoon: Persoon; onWeg?: () => void; wegLabel?: string }) {
  return (
    <span className="s-persoonchip">
      <Avatar naam={persoon.naam} />
      <span className="s-persoonchip__naam">{persoon.naam}</span>
      {onWeg && (
        <button type="button" className="s-persoonchip__weg" aria-label={wegLabel ?? `Verwijder ${persoon.naam}`} onClick={onWeg}>
          <Icoon als={X} klein />
        </button>
      )}
    </span>
  );
}

export type KeuzeKaart<T extends string> = { waarde: T; titel: ReactNode; uitleg?: ReactNode };

/**
 * Eén keuze uit een paar, als kaarten op toon (geen rondjes). Onder de kaart zit een
 * echt keuzerondje: toetsenbord en schermlezer werken zoals bij een gewone keuze.
 * `compact`: alleen de titel, voor lijstjes als rol of propositie.
 */
export function KeuzeKaarten<T extends string>({
  label,
  naam,
  opties,
  waarde,
  onKies,
  vast = false,
  kolommen,
  compact = false,
  uitleg,
}: {
  label: string;
  /** De naam van de groep keuzerondjes (uniek op de pagina). */
  naam: string;
  opties: readonly KeuzeKaart<T>[];
  waarde: T | null;
  onKies: (w: T) => void;
  vast?: boolean;
  kolommen?: 1 | 2 | 3;
  compact?: boolean;
  /** Eén zin onder de kaarten. */
  uitleg?: ReactNode;
}) {
  const id = useId();
  const kol = kolommen ?? (Math.min(opties.length, 3) as 1 | 2 | 3);
  return (
    <fieldset className="s-keuzeblok" disabled={vast}>
      <legend className="s-lbl">{label}</legend>
      <div className={`s-keuzekaarten s-keuzekaarten--${kol}${compact ? " s-keuzekaarten--compact" : ""}`}>
        {opties.map((o, i) => (
          <label key={o.waarde} className={`s-kk${o.waarde === waarde ? " s-kk--aan" : ""}`}>
            <input
              type="radio"
              className="s-kk__invoer"
              name={naam}
              value={o.waarde}
              checked={o.waarde === waarde}
              disabled={vast}
              aria-labelledby={`${id}-${i}`}
              aria-describedby={o.uitleg ? `${id}-${i}-u` : undefined}
              onChange={() => onKies(o.waarde)}
            />
            <span className="s-kk__titel" id={`${id}-${i}`}>
              {o.titel}
            </span>
            {o.uitleg && (
              <span className="s-kk__uitleg" id={`${id}-${i}-u`}>
                {o.uitleg}
              </span>
            )}
          </label>
        ))}
      </div>
      {uitleg && <p className="s-veldblok__uitleg">{uitleg}</p>}
    </fieldset>
  );
}

/** Een label boven een invoer, met eventueel één zin uitleg eronder. */
export function Veldblok({ label, uitleg, children }: { label: string; uitleg?: ReactNode; children: ReactNode }) {
  return (
    <label className="s-veldblok">
      <span className="s-lbl">{label}</span>
      {children}
      {uitleg && <span className="s-veldblok__uitleg">{uitleg}</span>}
    </label>
  );
}

/** Een invoerveld op toon: 36 hoog, geen rand. */
export function Invoer({ className, icoon, ...rest }: InputHTMLAttributes<HTMLInputElement> & { icoon?: LucideIcon }) {
  if (!icoon) return <input className={`s-invoer${className ? ` ${className}` : ""}`} {...rest} />;
  return (
    <span className={`s-invoer s-invoer--zoek${className ? ` ${className}` : ""}`}>
      <Icoon als={icoon} klein />
      <input {...rest} />
    </span>
  );
}

/** Aan of uit, met een titel en een zin uitleg (in plaats van een los vinkje). */
export function Schakelaar({
  aan,
  onWissel,
  titel,
  uitleg,
}: {
  aan: boolean;
  onWissel: (aan: boolean) => void;
  titel: string;
  uitleg?: ReactNode;
}) {
  const id = useId();
  return (
    <label className="s-schakelaar">
      <input
        type="checkbox"
        role="switch"
        className="s-schakelaar__invoer"
        checked={aan}
        aria-labelledby={`${id}-t`}
        aria-describedby={uitleg ? `${id}-u` : undefined}
        onChange={(e) => onWissel(e.target.checked)}
      />
      <span className="s-schakelaar__baan" aria-hidden="true" />
      <span className="s-schakelaar__tekst">
        <span className="s-schakelaar__titel" id={`${id}-t`}>
          {titel}
        </span>
        {uitleg && (
          <span className="s-schakelaar__uitleg" id={`${id}-u`}>
            {uitleg}
          </span>
        )}
      </span>
    </label>
  );
}

/** Een rustige notitie op toon (wat er gebeurt, waarom iets leeg is). */
export function Notitie({ children, kop }: { children: ReactNode; kop?: string }) {
  return (
    <div className="s-notitie" role="note">
      {kop && <span className="s-notitie__kop">{kop}</span>}
      {children}
    </div>
  );
}

/** Een foutmelding van de server, letterlijk. */
export function Foutmelding({ children }: { children: ReactNode }) {
  return (
    <p role="alert" className="s-fout">
      {children}
    </p>
  );
}

let kaderGeladen = false;

/** De lichte tokens één keer, alleen binnen .s-kader: het oude scherm kent geen donker. */
function laadKader(): void {
  if (kaderGeladen) return;
  kaderGeladen = true;
  const licht = tokens.match(/:root\s*\{([^}]*)\}/)?.[1] ?? "";
  const el = document.createElement("style");
  el.dataset.platformStijl = "";
  el.textContent = `.s-kader{${licht}}`;
  document.head.appendChild(el);
}

/**
 * Het kader voor een scherm van de huidige weergave (Beheer) dat bouwstenen gebruikt.
 * Daar laadt tokens.css niet; zonder dit kader vallen knoppen, velden en vensters
 * terug op de kale browserstijl (de owner zag dat in "Plan instellen", 04-11).
 */
export function PlatformStijl({ children }: { children: ReactNode }) {
  useLayoutEffect(laadKader, []);
  return <div className="s-kader">{children}</div>;
}
